/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useCallback, useMemo } from "react";
import dayjs from "dayjs";

type Props = {
  current: dayjs.Dayjs;
  year: string;
  forecast: number[];
  spends: number[];
};

export function useBudgetChart({ current, year, forecast, spends }: Props) {
  const OPACITY = {
    from: 0.23,
    to: 0.35,
  };

  const currentMonthIndex = current.month();
  const currentYear = current.year();

  const getForecast = (index: number) => {
    if (index === currentMonthIndex && Number(year) >= currentYear) {
      return forecast[index];
    }

    return spends[index] === 0 ? forecast[index] : 0;
  };

  const isBefore = useCallback(
    (index: number) =>
      (index < currentMonthIndex && Number(year) <= currentYear) ||
      Number(year) < currentYear,
    [currentMonthIndex, currentYear, year]
  );

  const isAfter = useCallback((index: number) => !isBefore(index), [isBefore]);

  const hasAnyBefore = useMemo(
    () =>
      forecast.some(
        (value, index) => isBefore(index) && value > 0 && spends[index] === 0
      ),
    [forecast, isBefore, spends]
  );

  const hasAnyAfter = useMemo(
    () =>
      forecast.some(
        (value, index) =>
          value > 0 &&
          ((isAfter(index) && spends[index] === 0) ||
            (index === currentMonthIndex && Number(year) >= currentYear))
      ),
    [currentMonthIndex, currentYear, forecast, isAfter, spends, year]
  );

  return {
    getForecast,
    isBefore,
    hasAnyBefore,
    hasAnyAfter,
    OPACITY,
  };
}
